import Constants from "src/utils/constants";
import Helper from "src/utils/helper";

export function isDraftEmailId(id) {
  return (
    typeof id === "string" &&
    id.startsWith(Constants.constants.EMAIL_DRAFT_ID_PREFIX)
  );
}

/**
 * Whether the message body holds anything a reader would see.
 *
 * Not a plain emptiness test: the body is Quill HTML, and an editor the author
 * has clicked into and left empty still yields `<p><br></p>`, which is truthy
 * and trims to itself. Tags and `&nbsp;` are stripped before the check.
 */
function hasBodyContent(html) {
  if (typeof html !== "string") return false;
  return Helper.stripHtml(html.replace(/&nbsp;/g, " ")).length > 0;
}

function findTokenProblems(text) {
  const problems = [];
  if (typeof text !== "string") return problems;

  for (const [token] of text.matchAll(/\$\([^)]*/g)) {
    const tagIndex = token.indexOf("<");
    if (tagIndex !== -1) {
      problems.push(
        `HTML is inside the token starting "${token.slice(0, tagIndex)}" — retype it as plain text.`
      );
    }
  }

  for (const loopMatch of text.matchAll(/\$\(([^)[\]]+)\)\.loop\(/g)) {
    const startIndex = loopMatch.index + loopMatch[0].length;
    let parenCount = 1;
    let endIndex = startIndex;

    while (endIndex < text.length && parenCount > 0) {
      if (text[endIndex] === "(") {
        parenCount++;
      } else if (text[endIndex] === ")") {
        parenCount--;
      }
      endIndex++;
    }

    if (parenCount > 0) {
      problems.push(`$(${loopMatch[1]}).loop( is never closed with ")".`);
    } else if (!text.substring(startIndex, endIndex - 1).trim()) {
      problems.push(
        `$(${loopMatch[1]}).loop() is empty — put the row template inside the parentheses.`
      );
    }
  }

  const ifOpened = (text.match(/\{\{#if\b/g) ?? []).length;
  const ifClosed = (text.match(/\{\{\/if\}\}/g) ?? []).length;
  if (ifOpened !== ifClosed) {
    problems.push(`${ifOpened} {{#if}} but ${ifClosed} {{/if}}.`);
  }

  const eachOpened = (text.match(/\{\{#each\b/g) ?? []).length;
  const eachClosed = (text.match(/\{\{\/each\}\}/g) ?? []).length;
  if (eachOpened !== eachClosed) {
    problems.push(`${eachOpened} {{#each}} but ${eachClosed} {{/each}}.`);
  }

  return problems;
}

export function validateEmailTemplate(tpl) {
  const errors = [];
  if (!tpl) return errors;
  if (!tpl.name?.trim()) {
    errors.push({ field: "name", message: "Template title is required." });
  }
  if (!tpl.template?.subject?.trim()) {
    errors.push({ field: "subject", message: "Subject is required." });
  }
  if (!hasBodyContent(tpl.template?.body)) {
    errors.push({ field: "body", message: "Message body is required." });
  }
  const subjectProblems = findTokenProblems(tpl.template?.subject);
  if (subjectProblems.length) {
    errors.push({
      field: "subject",
      message: `Subject: ${subjectProblems.join(" ")}`
    });
  }
  const bodyProblems = findTokenProblems(tpl.template?.body);
  if (bodyProblems.length) {
    errors.push({
      field: "body",
      message: `Message body: ${bodyProblems.join(" ")}`
    });
  }
  return errors;
}

/**
 * Splits a default_sending_data recipient array into the three kinds of entry
 * the builder treats differently.
 *
 * `tokens` — symbolic recipients from `EMAIL_RECIPIENT_TYPES`, resolved per
 *   send by `Helper.resolveRecipient`. Offered as checkboxes.
 * `groups` — mail_distribution_group ids, expanded to members by
 *   `Helper.convertGroupIdsToEmails`. Edited through the group picker in
 *   `RecipientsEditor.vue` rather than the To / CC chips.
 * `manual` — plain addresses someone typed, edited as chips.
 *
 * Every enum token is recognised, not just the three with a checkbox, so an
 * unoffered one (ME) survives a save instead of being mistaken for an address.
 */
export function splitRecipients(list) {
  const manual = [];
  const tokens = [];
  const groups = [];
  if (!Array.isArray(list)) return { manual, tokens, groups };
  const known = Object.values(Constants.enums.EMAIL_RECIPIENT_TYPES);
  for (const entry of list) {
    if (typeof entry === "number") {
      groups.push(entry);
    } else if (typeof entry !== "string") {
      continue;
    } else if (known.includes(entry)) {
      tokens.push(entry);
    } else if (entry.trim()) {
      manual.push(entry.trim());
    }
  }
  return { manual, tokens, groups };
}

export function sendingDataHasNoRecipients(sending) {
  return [sending?.to, sending?.cc].every((list) => {
    const { manual, tokens, groups } = splitRecipients(list);
    return !manual.length && !tokens.length && !groups.length;
  });
}

export function templateHasNoRecipients(tpl) {
  return sendingDataHasNoRecipients(tpl?.default_sending_data ?? {});
}

// `$(a).b`, `$(a.b)` and `a.b` all give "a" — the part the variable list offers.
export function sendConfigVariableKey(variable) {
  if (typeof variable !== "string") return "";
  const text = variable.trim();
  const wrapped = text.match(/^\$\(([^)]*)\)(.*)$/);
  const path = wrapped ? `${wrapped[1]}${wrapped[2]}` : text;
  return path.split(".")[0];
}

export function collectDraftedEmails(template, pages) {
  const drafts = new Map();
  const missing = new Set();

  const walk = (node) => {
    if (!node || typeof node !== "object") return;
    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }
    if (isDraftEmailId(node.email_template_id)) {
      if (node.draft_email) {
        if (!drafts.has(node.email_template_id)) {
          drafts.set(node.email_template_id, {
            provisional_id: node.email_template_id,
            ...Helper.deepCopy(node.draft_email)
          });
        }
      } else {
        missing.add(node.email_template_id);
      }
    }
    for (const [key, value] of Object.entries(node)) {
      if (key !== "draft_email") walk(value);
    }
  };

  walk(template);
  walk(pages);

  return {
    drafts: [...drafts.values()],
    missing: [...missing].filter((id) => !drafts.has(id))
  };
}

// Same lookup rules as the send-time Helper.findAndSetKeyInObject: an "a.b"
// key searches for "a", and `_private_` keys are not searched into.
function hasKeyDeep(node, key) {
  if (!node || typeof node !== "object") return false;
  const mainKey = key.split(".")[0];
  if (
    !Array.isArray(node) &&
    Object.prototype.hasOwnProperty.call(node, mainKey)
  ) {
    return true;
  }
  return Object.entries(node).some(
    ([childKey, child]) =>
      !childKey.startsWith("_private_") && hasKeyDeep(child, key)
  );
}

/**
 * Every distinct `$(…)` token in a template's subject or body whose key
 * `isKnown(key)` rejects.
 */
export function findUnresolvedTokens(tpl, isKnown) {
  const text = `${tpl?.template?.subject ?? ""} ${tpl?.template?.body ?? ""}`;
  const unresolved = new Set();
  for (const [token, key] of text.matchAll(
    Constants.constants.EMAIL_TOKEN_PATTERN
  )) {
    if (!isKnown(key)) unresolved.add(token);
  }
  return [...unresolved];
}

export function findMissingTokens(draft, sample) {
  return findUnresolvedTokens(draft, (key) => hasKeyDeep(sample, key));
}

export function applyPublishedEmailIds(node, idMap) {
  if (Array.isArray(node)) {
    return node.map((child) => applyPublishedEmailIds(child, idMap));
  }
  if (!node || typeof node !== "object") return node;

  const realId = idMap?.[node.email_template_id];
  const out = {};
  for (const [key, value] of Object.entries(node)) {
    if (realId && key === "draft_email") continue;
    out[key] = applyPublishedEmailIds(value, idMap);
  }
  if (realId) out.email_template_id = realId;
  return out;
}
